/**
 * ProjectDetailPage — per-project management view.
 *
 * June 8, 2026 "feel alive" pass — mirrors the ProjectHub redesign so
 * the user keeps the same warm voice when they zoom into a single
 * project. The previous shape (SurfaceHeader + 7 phase-tagged
 * accordions) read like a settings panel; the new shape leads with a
 * narrative glance:
 *
 *   - LoginBlob brand anchor + project icon + project name
 *   - CompanionHero subhero with one calm sentence built from the live
 *     ``Project`` fields (last conversation title, memory count,
 *     overdue tasks)
 *   - Primary "Riprendi" CTA pointing at ``last_conversation_id`` so
 *     the user lands one click away from the work they were doing
 *   - Section accordion keeps the deep-dive surface, but each row now
 *     shows a live subtitle ("ultimo: 'Calcola IVA Q2', 2h fa") and
 *     the developer-jargon Phase chip is gone
 *
 * Sections still ship as the existing ``<*Section>`` components — the
 * accordion infrastructure is preserved because the value of this page
 * is the depth, not the chrome. We just made it talk to the user.
 */

import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Folder,
  Images,
  Loader2,
  ListChecks,
  Sparkles,
  Timer,
  Users as UsersIcon,
  Workflow,
} from "lucide-react";
import { motion } from "framer-motion";
import * as React from "react";

import type { Project } from "@ando/core/api";
import {
  Button,
  CompanionHero,
  SurfaceEmptyState,
  SurfaceHeader,
  SurfaceLayout,
  cn,
  useReducedMotion,
} from "@ando/ui";
import { LoginBlob } from "@ando/ui/blob";

import { useChatStore } from "@/features/chat";

import { useProjects } from "../hooks/useProjects.js";
import { useSessionUserId } from "@/features/connectors/hooks/useSessionUserId";

import {
  selectSetCurrentProjectId,
  useProjectsStore,
} from "../state/projectsStore.js";
import { formatShortRelative, formatShortUntil } from "../utils/activity.js";
import { ProjectIcon } from "./ProjectIcon.js";
import { ArtifactGallerySection } from "./sections/ArtifactGallerySection.js";
import { AutomationsSection } from "./sections/AutomationsSection.js";
import { KnowledgeSection } from "./sections/KnowledgeSection.js";
import { MembersSection } from "./sections/MembersSection.js";
import { MemorySection } from "./sections/MemorySection.js";
import { PersonaSection } from "./sections/PersonaSection.js";
import { TasksSection } from "./sections/TasksSection.js";

// ─── Section catalog ──────────────────────────────────────────────────────
//
// ``subtitleFor`` builds a live narrative from the ``Project`` row so
// the user can scan the page like a status report without expanding
// anything. Each row falls back to a calm default copy when the live
// fields are absent (e.g. brand-new project with no memory yet).

interface SectionDef {
  readonly id: string;
  readonly title: React.ReactNode;
  readonly subtitleFor: (project: Project, i18n: I18n) => React.ReactNode;
  readonly icon: React.ComponentType<{ className?: string }>;
  /** Default-open at mount. Persona stays open so the user sees the
   *  most-asked-for slot first; the rest collapse to keep the page calm. */
  readonly defaultOpen: boolean;
  readonly Body: React.ComponentType<{
    project: Project;
    currentUserId: string;
  }>;
}

function defaultPersonaSubtitle(project: Project, i18n: I18n): React.ReactNode {
  const role = (project.persona_role ?? "").trim();
  const tone = (project.persona_tone ?? "").trim();
  if (role && tone) return i18n._(msg`${role} · tono ${tone}`);
  if (role) return role;
  if (tone) return <Trans>Tono: {tone}</Trans>;
  return <Trans>Come l'agente deve comportarsi su questo progetto</Trans>;
}

function knowledgeSubtitle(project: Project, i18n: I18n): React.ReactNode {
  const conn = project.default_connection_ids.length;
  const fold = project.default_folder_filter.length;
  if (conn === 0 && fold === 0) {
    return <Trans>Nessun connettore né cartella allegata</Trans>;
  }
  const parts: string[] = [];
  parts.push(
    conn === 1 ? i18n._(msg`1 connettore`) : i18n._(msg`${conn} connettori`),
  );
  parts.push(
    fold === 1 ? i18n._(msg`1 cartella`) : i18n._(msg`${fold} cartelle`),
  );
  return parts.join(" · ");
}

function artifactsSubtitle(project: Project): React.ReactNode {
  const name = (project.last_artifact_filename ?? "").trim();
  const at = formatShortRelative(project.last_artifact_at);
  if (name.length > 0) {
    return (
      <>
        <Trans>Ultimo</Trans>:{" "}
        <span className="text-ink-2">"{name}"</span>
        {at ? <span className="text-ink-3"> · {at}</span> : null}
      </>
    );
  }
  return <Trans>Nessun documento generato ancora</Trans>;
}

function tasksSubtitle(project: Project, i18n: I18n): React.ReactNode {
  const overdue = project.overdue_tasks_count ?? 0;
  const pending = project.pending_tasks_count ?? 0;
  if (overdue === 0 && pending === 0) {
    return <Trans>Nessuna attività in corso</Trans>;
  }
  const parts: React.ReactNode[] = [];
  if (overdue > 0) {
    parts.push(
      <span key="overdue" className="text-error">
        {overdue === 1
          ? i18n._(msg`1 in ritardo`)
          : i18n._(msg`${overdue} in ritardo`)}
      </span>,
    );
  }
  if (pending > 0) {
    parts.push(
      <span key="pending" className="text-ink-2">
        {pending === 1
          ? i18n._(msg`1 in coda`)
          : i18n._(msg`${pending} in coda`)}
      </span>,
    );
  }
  return parts.reduce<React.ReactNode[]>((acc, node, i) => {
    if (i === 0) return [node];
    acc.push(
      <span key={`sep-${i}`} className="text-ink-3">
        {" · "}
      </span>,
      node,
    );
    return acc;
  }, []);
}

function memorySubtitle(project: Project, i18n: I18n): React.ReactNode {
  const count = project.memory_count ?? 0;
  const at = formatShortRelative(project.last_memory_at);
  if (count === 0) {
    return <Trans>Ando non ha ancora memorizzato nulla</Trans>;
  }
  const label =
    count === 1 ? i18n._(msg`1 ricordo`) : i18n._(msg`${count} ricordi`);
  if (at) {
    return (
      <>
        <span className="text-ink-2">{label}</span>
        <span className="text-ink-3">
          {" "}
          · <Trans>ultimo {at}</Trans>
        </span>
      </>
    );
  }
  return <span className="text-ink-2">{label}</span>;
}

function automationsSubtitle(project: Project, i18n: I18n): React.ReactNode {
  const active = project.active_flows_count ?? 0;
  const at = formatShortUntil(project.next_run_at);
  if (active === 0) {
    return <Trans>Nessuna automazione attiva</Trans>;
  }
  const label =
    active === 1 ? i18n._(msg`1 attiva`) : i18n._(msg`${active} attive`);
  if (at) {
    return (
      <>
        <span className="text-ink-2">{label}</span>
        <span className="text-ink-3">
          {" "}
          · <Trans>prossima {at}</Trans>
        </span>
      </>
    );
  }
  return <span className="text-ink-2">{label}</span>;
}

const SECTIONS: ReadonlyArray<SectionDef> = [
  {
    id: "persona",
    title: <Trans>Persona</Trans>,
    subtitleFor: defaultPersonaSubtitle,
    icon: Sparkles,
    defaultOpen: true,
    Body: ({ project }) => <PersonaSection project={project} />,
  },
  {
    id: "knowledge",
    title: <Trans>Conoscenza</Trans>,
    subtitleFor: knowledgeSubtitle,
    icon: Folder,
    defaultOpen: false,
    Body: ({ project }) => <KnowledgeSection project={project} />,
  },
  {
    id: "artifacts",
    title: <Trans>Documenti generati</Trans>,
    subtitleFor: artifactsSubtitle,
    icon: Images,
    defaultOpen: false,
    Body: ({ project }) => (
      <ArtifactGallerySection projectId={project.project_id} />
    ),
  },
  {
    id: "tasks",
    title: <Trans>Attività</Trans>,
    subtitleFor: tasksSubtitle,
    icon: ListChecks,
    defaultOpen: false,
    Body: ({ project }) => <TasksSection projectId={project.project_id} />,
  },
  {
    id: "memory",
    title: <Trans>Memoria del progetto</Trans>,
    subtitleFor: memorySubtitle,
    icon: Timer,
    defaultOpen: false,
    Body: ({ project }) => (
      <MemorySection projectId={project.project_id} project={project} />
    ),
  },
  {
    id: "automations",
    title: <Trans>Automazioni</Trans>,
    subtitleFor: automationsSubtitle,
    icon: Workflow,
    defaultOpen: false,
    Body: ({ project }) => (
      <AutomationsSection projectId={project.project_id} />
    ),
  },
  {
    id: "members",
    title: <Trans>Membri</Trans>,
    subtitleFor: () => (
      <Trans>Persone con accesso a questo progetto</Trans>
    ),
    icon: UsersIcon,
    defaultOpen: false,
    Body: ({ project, currentUserId }) => (
      <MembersSection project={project} currentUserId={currentUserId} />
    ),
  },
];

// ─── Companion narrative ──────────────────────────────────────────────────
//
// Builds the one-sentence glance the CompanionHero renders. The copy is
// intentionally calm and human — "L'ultima volta ci hai lavorato 2h fa,
// stavamo guardando «Calcola IVA Q2»." — and degrades gracefully when
// fields are missing (brand-new project, no activity yet).

function buildCompanionGlance(project: Project, i18n: I18n): React.ReactNode {
  const lastConvTitle = (project.last_conversation_title ?? "").trim();
  const lastAt = formatShortRelative(project.last_active_at);
  const overdue = project.overdue_tasks_count ?? 0;
  const memCount = project.memory_count ?? 0;

  // Brand-new project — no conversation, no memory yet.
  if (!lastConvTitle && memCount === 0 && overdue === 0) {
    return (
      <Trans>
        Questo spazio è ancora silenzioso. Apri una chat e dimmi a cosa stai
        lavorando — comincio a ricordare da lì.
      </Trans>
    );
  }

  // Compose the sentence in fragments so React can highlight <strong>.
  const fragments: React.ReactNode[] = [];

  if (lastConvTitle) {
    const when = lastAt || i18n._(msg`poco fa`);
    fragments.push(
      <Trans key="last">
        L'ultima volta ci hai lavorato <strong>{when}</strong>,
        stavamo guardando "<strong>{lastConvTitle}</strong>".
      </Trans>,
    );
  } else if (lastAt) {
    fragments.push(
      <Trans key="last-bare">
        Hai aperto questo progetto <strong>{lastAt}</strong>.
      </Trans>,
    );
  }

  if (overdue > 0) {
    fragments.push(" ");
    fragments.push(
      overdue === 1 ? (
        <Trans key="overdue">
          C'è <strong>1 attività in ritardo</strong>: vale la pena darle un'occhiata.
        </Trans>
      ) : (
        <Trans key="overdue-plural">
          Ci sono <strong>{overdue} attività in ritardo</strong>: vale la pena guardarle.
        </Trans>
      ),
    );
  } else if (memCount > 0) {
    fragments.push(" ");
    fragments.push(
      memCount === 1 ? (
        <Trans key="mem">
          Ho memorizzato <strong>1 ricordo</strong> su questo progetto.
        </Trans>
      ) : (
        <Trans key="mem-plural">
          Ho memorizzato <strong>{memCount} ricordi</strong> su questo progetto.
        </Trans>
      ),
    );
  }

  if (fragments.length === 0) {
    return (
      <Trans>Pronti a riprendere — dimmi tu da dove cominciamo.</Trans>
    );
  }

  return <>{fragments}</>;
}

// ─── Component ────────────────────────────────────────────────────────────

const ENTER_TRANSITION = { duration: 0.22, ease: [0.32, 0.72, 0, 1] as const };

export const ProjectDetailPage: React.FC = () => {
  const { i18n } = useLingui();
  const params = useParams({ from: "/_authed/workspace/projects/$projectId" });
  const projectId = params.projectId;
  const navigate = useNavigate();
  const query = useProjects();
  const setCurrentProjectId = useProjectsStore(selectSetCurrentProjectId);
  const reducedMotion = useReducedMotion();
  // Resolved at the page boundary so section bodies receive a stable
  // string and don't each re-trigger the session-info hook on mount.
  const currentUserId = useSessionUserId();

  const project = React.useMemo<Project | null>(() => {
    if (!query.data) return null;
    return query.data.find((p) => p.project_id === projectId) ?? null;
  }, [query.data, projectId]);

  // Keep the global project lens in sync with the URL — landing on
  // ``/workspace/projects/$projectId`` is a strong intent signal that the
  // user is working in that project, so the ProjectPill should reflect it.
  React.useEffect(() => {
    if (project) setCurrentProjectId(project.project_id);
  }, [project, setCurrentProjectId]);

  // ── Loading state — full-surface spinner overkill for a list query
  //    that usually resolves in <50ms. Skeleton mirrors the new hero
  //    so the page doesn't jump on first paint.
  if (query.isLoading && !query.data) {
    return (
      <SurfaceLayout
        header={
          <SurfaceHeader
            label={<Trans>Progetti</Trans>}
            title={
              <span className="inline-flex items-center gap-2 text-ink-3">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                <Trans>Caricamento progetto…</Trans>
              </span>
            }
          />
        }
        contentAriaLabel={i18n._(msg`Dettagli progetto in caricamento`)}
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 pt-1" aria-hidden>
          <div className="h-24 animate-pulse rounded-2xl border border-hairline bg-white/40" />
          <div className="h-14 animate-pulse rounded-xl border border-hairline bg-white/40" />
          {SECTIONS.map((s) => (
            <div
              key={s.id}
              className="h-16 animate-pulse rounded-xl border border-hairline bg-white/40"
            />
          ))}
        </div>
      </SurfaceLayout>
    );
  }

  // ── Not-found / archived — empty-state with a clear back path.
  if (!project) {
    return (
      <SurfaceLayout
        header={
          <SurfaceHeader
            label={<Trans>Progetti</Trans>}
            title={<Trans>Progetto non trovato</Trans>}
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: "/workspace/projects" })}
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                <Trans>Torna ai progetti</Trans>
              </Button>
            }
          />
        }
        contentAriaLabel={i18n._(msg`Progetto inesistente`)}
      >
        <SurfaceEmptyState
          icon={<Folder className="h-5 w-5" />}
          title={<Trans>Il progetto non esiste più</Trans>}
          description={
            <Trans>
              Potrebbe essere stato archiviato. Apri l'elenco dei progetti per
              vederli tutti.
            </Trans>
          }
          action={
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate({ to: "/workspace/projects" })}
            >
              <Trans>Vai ai progetti</Trans>
            </Button>
          }
        />
      </SurfaceLayout>
    );
  }

  return (
    <ProjectDetailContent
      project={project}
      currentUserId={currentUserId ?? ""}
      navigate={navigate}
      reducedMotion={reducedMotion}
    />
  );
};

ProjectDetailPage.displayName = "ProjectDetailPage";

// ───────────────────────────────────────────────────────────────────────────
// Detail content — split so the hero only renders when the project is
// resolved. Keeps the hooks order stable in the parent across the
// loading / not-found / found branches.
// ───────────────────────────────────────────────────────────────────────────

interface ProjectDetailContentProps {
  readonly project: Project;
  readonly currentUserId: string;
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly reducedMotion: boolean;
}

const ProjectDetailContent: React.FC<ProjectDetailContentProps> = ({
  project,
  currentUserId,
  navigate,
  reducedMotion,
}) => {
  const { i18n } = useLingui();
  const selectConversation = useChatStore((s) => s.selectConversation);

  // ── Resume / start CTA ────────────────────────────────────────────────
  // When the project has a last conversation we drop the user back into
  // it. Without one we offer a "Apri chat in questo progetto" link that
  // pre-selects the project so the next composer.send creates the conv
  // under it (mirrors the v2 ConversationSidebar pattern, see the
  // ``feedback_v2_chat_must_register_via_create_conversation`` memo).
  const lastConvId = project.last_conversation_id;
  const handleResume = React.useCallback(() => {
    if (lastConvId) {
      selectConversation(lastConvId);
    }
    void navigate({ to: "/" });
  }, [lastConvId, navigate, selectConversation]);

  const heroMotion = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: ENTER_TRANSITION,
      };

  const subheroMotion = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
        transition: { ...ENTER_TRANSITION, delay: 0.05 },
      };

  const description = (project.description ?? "").trim();
  const conversationCount = project.conversation_count ?? 0;

  return (
    <SurfaceLayout contentAriaLabel={i18n._(msg`Sezioni del progetto ${project.name}`)}>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 pt-2 pb-6">
        {/* ── Hero — LoginBlob + project identity + back link ───── */}
        <motion.header
          {...heroMotion}
          className="flex flex-col gap-3"
          aria-labelledby="project-hero-title"
        >
          <button
            type="button"
            onClick={() => navigate({ to: "/workspace/projects" })}
            className="-mx-1 inline-flex w-fit items-center gap-1 rounded px-1 py-0.5 text-[12px] text-ink-3 transition-colors hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
          >
            <ArrowLeft className="h-3 w-3" aria-hidden />
            <Trans>Tutti i progetti</Trans>
          </button>

          <div className="flex items-start gap-3">
            <ProjectIcon
              iconName={project.icon}
              colorToken={project.color}
              size={26}
              className="mt-[18px] shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.08em]">
                <Trans>Progetto</Trans>
              </div>
              <div className="mt-0.5 flex items-center gap-2.5">
                <h1
                  id="project-hero-title"
                  className="truncate font-display font-semibold text-[26px] text-ink-1 leading-[1.15] tracking-[-0.012em]"
                >
                  {project.name}
                </h1>
                {/* Agent presence — small blob to the right of the title */}
                <span aria-hidden className="block h-7 w-7 shrink-0">
                  <LoginBlob size="mini" />
                </span>
              </div>
              {description ? (
                <p className="mt-1 line-clamp-2 text-[13px] text-ink-2 leading-snug">
                  {description}
                </p>
              ) : null}
            </div>
          </div>
        </motion.header>

        {/* ── Companion glance — one calm sentence + Riprendi CTA ── */}
        <motion.div {...subheroMotion}>
          <CompanionHero
            icon={<Sparkles className="h-3.5 w-3.5" />}
            cta={
              lastConvId ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleResume}
                  className="rounded-full"
                >
                  <Trans>Riprendi</Trans>
                  <ArrowRight className="ml-1 h-3 w-3" aria-hidden />
                </Button>
              ) : conversationCount === 0 ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleResume}
                  className="rounded-full"
                >
                  <Trans>Apri chat</Trans>
                  <ArrowRight className="ml-1 h-3 w-3" aria-hidden />
                </Button>
              ) : undefined
            }
          >
            {buildCompanionGlance(project, i18n)}
          </CompanionHero>
        </motion.div>

        {/* ── Section accordion ──────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          {SECTIONS.map((section, i) => (
            <motion.div
              key={section.id}
              initial={reducedMotion ? false : { opacity: 0, y: 6 }}
              animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
              transition={
                reducedMotion
                  ? undefined
                  : { ...ENTER_TRANSITION, delay: 0.1 + i * 0.03 }
              }
            >
              <SectionCard
                def={section}
                project={project}
                currentUserId={currentUserId}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </SurfaceLayout>
  );
};

ProjectDetailContent.displayName = "ProjectDetailContent";

// ───────────────────────────────────────────────────────────────────────────
// SectionCard — local accordion primitive
// ───────────────────────────────────────────────────────────────────────────

interface SectionCardProps {
  readonly def: SectionDef;
  readonly project: Project;
  readonly currentUserId: string;
}

const SectionCard: React.FC<SectionCardProps> = ({
  def,
  project,
  currentUserId,
}) => {
  const { i18n } = useLingui();
  const [open, setOpen] = React.useState(def.defaultOpen);
  const Icon = def.icon;
  const headerId = `project-section-${def.id}-header`;
  const panelId = `project-section-${def.id}-panel`;
  const subtitle = def.subtitleFor(project, i18n);

  return (
    <section className="overflow-hidden rounded-xl border border-hairline bg-white/70 shadow-sm">
      <h2 id={headerId} className="m-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          className={cn(
            "flex w-full items-center gap-3 px-5 py-4 text-left",
            "transition-colors hover:bg-ink-1/[0.03]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-inset",
          )}
        >
          <span
            aria-hidden
            className="inline-flex shrink-0 items-center justify-center text-ink-3 [&_svg]:[stroke-width:1.5]"
          >
            <Icon className="h-[18px] w-[18px]" />
          </span>
          <span className="min-w-0 flex-1">
            <div className="font-[550] font-display text-[14px] text-ink-1 leading-tight">
              {def.title}
            </div>
            <div className="mt-0.5 truncate text-[12px] text-ink-3">
              {subtitle}
            </div>
          </span>
          <span aria-hidden className="shrink-0 text-ink-3">
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
        </button>
      </h2>
      {open ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          className="border-hairline border-t px-5 py-5"
        >
          <def.Body project={project} currentUserId={currentUserId} />
        </div>
      ) : null}
    </section>
  );
};
